import {
  LocationApiClient,
  LocationsFilterParams,
  createLocationApiClient,
} from '@peritasai/data-access';
import {
  CreateLocationDto,
  LocationResponseDto,
  UpdateLocationDto,
} from '@peritasai/types';
import { LocationEntity } from './location.entity';

export interface ILocationRepository {
  getLocations(params?: LocationsFilterParams): Promise<{ data: LocationEntity[]; total: number; page: number; limit: number }>;
  getAllLocations(params?: Omit<LocationsFilterParams, 'page' | 'limit'>): Promise<LocationEntity[]>;
  getLocationById(id: string): Promise<LocationEntity>;
  createLocation(dto: CreateLocationDto): Promise<LocationEntity>;
  updateLocation(id: string, dto: UpdateLocationDto): Promise<LocationEntity>;
}

export class LocationRepository implements ILocationRepository {
  constructor(private readonly locationApiClient: LocationApiClient) {}

  async getLocations(
    params?: LocationsFilterParams
  ): Promise<{ data: LocationEntity[]; total: number; page: number; limit: number }> {
    const response = await this.locationApiClient.getLocations(params);
    return {
      data: response.data.map((dto) => this.toEntity(dto)),
      total: response.total,
      page: response.page,
      limit: response.limit,
    };
  }

  async getAllLocations(
    params?: Omit<LocationsFilterParams, 'page' | 'limit'>
  ): Promise<LocationEntity[]> {
    const PAGE_SIZE = 100;
    const all: LocationEntity[] = [];
    let page = 1;

    while (true) {
      const response = await this.locationApiClient.getLocations({ ...params, page, limit: PAGE_SIZE });
      all.push(...response.data.map((dto) => this.toEntity(dto)));
      if (all.length >= response.total) break;
      page++;
    }

    return all;
  }

  async getLocationById(id: string): Promise<LocationEntity> {
    const locationResponse = await this.locationApiClient.getLocationById(id);
    return this.toEntity(locationResponse);
  }

  async createLocation(dto: CreateLocationDto): Promise<LocationEntity> {
    const response = await this.locationApiClient.createLocation(dto);
    return this.toEntity(response);
  }

  async updateLocation(id: string, dto: UpdateLocationDto): Promise<LocationEntity> {
    const response = await this.locationApiClient.updateLocation(id, dto);
    return this.toEntity(response);
  }

  private toEntity(dto: LocationResponseDto): LocationEntity {
    return new LocationEntity(dto);
  }
}

const defaultLocationApiClient = createLocationApiClient();

export const locationRepository = new LocationRepository(defaultLocationApiClient);
